import React, { useId, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, ButtonBase, Collapse, Divider, Stack, Typography } from '@mui/material';

import type { LeDataContentBlock, LeDataProvenance, LeDataUsageSummary } from '~/types/leData';
import type { Lens } from '~/types/leLens';

import ContentBlockView from '../chat/ContentBlockView';
import { DUR_STATE, EASE_STATE } from '../chat/uiTokens';

import { LOW_CONFIDENCE_CAVEAT, receiptCta, receiptFacts } from './receiptCopy';

interface AnswerReceiptProps {
  blocks: Array<LeDataContentBlock>;
  provenance?: LeDataProvenance;
  tablesInline: boolean;
  debugUsage?: LeDataUsageSummary;
  onSavedLens?: (lens: Lens) => void;
  methodElsewhere?: boolean;
}

function narrationOf(block: LeDataContentBlock): string {
  const text = block.payload.markdown ?? block.payload.content;
  return typeof text === 'string' ? text.trim() : '';
}

function withoutDialect(block: LeDataContentBlock): LeDataContentBlock {
  const { dialect: _dialect, ...payload } = block.payload;
  return { ...block, payload };
}

type ReceiptContentProps = Omit<AnswerReceiptProps, 'onSavedLens' | 'methodElsewhere'>;

function receiptContents({ blocks, provenance, tablesInline, debugUsage }: ReceiptContentProps) {
  const { facts, confidence, tablesTitle } = receiptFacts(provenance);
  const narration = blocks
    .filter((block) => block.type === 'text' && block.payload.section === 'thought-process')
    .map(narrationOf)
    .filter(Boolean);
  const queries = blocks.filter((block) => block.type === 'sql');
  const tables = blocks.filter((block) => block.type === 'table');
  const extraTables = tablesInline ? tables.slice(0, -1) : tables;
  const hasMethod = narration.length > 0 || queries.length > 0 || extraTables.length > 0;
  const quietFacts = debugUsage ? [...facts, `$${debugUsage.costUsd.toFixed(4)}`] : facts;
  return { confidence, tablesTitle, narration, queries, extraTables, hasMethod, quietFacts };
}

export function hasAnswerReceipt(props: ReceiptContentProps, methodElsewhere = false): boolean {
  const { hasMethod, quietFacts } = receiptContents(props);
  return (hasMethod && !methodElsewhere) || quietFacts.length > 0;
}

export function hasAnswerMethod(props: ReceiptContentProps): boolean {
  return receiptContents(props).hasMethod;
}

export function AnswerMethod({ onSavedLens, ...props }: ReceiptContentProps & Pick<AnswerReceiptProps, 'onSavedLens'>) {
  const { narration, queries, extraTables } = receiptContents(props);
  return (
    <Stack spacing={1.25} divider={<Divider flexItem />} sx={{ pt: 1.25, pb: 0.5 }} data-testid="ui-stream-method">
      {narration.length > 0 ? (
        <Stack spacing={0.5} data-testid="ui-stream-method-narration">
          {narration.map((line, index) => (
            <Typography key={index} sx={{ fontSize: 13, color: 'text.secondary' }}>
              {line}
            </Typography>
          ))}
        </Stack>
      ) : null}
      {queries.map((block) => (
        <Box key={block.id} data-testid="ui-stream-method-sql">
          <ContentBlockView block={withoutDialect(block)} frameless onSavedLens={onSavedLens} />
        </Box>
      ))}
      {extraTables.map((block) => (
        <Box key={block.id} data-testid="ui-stream-method-table">
          <ContentBlockView block={block} frameless onSavedLens={onSavedLens} />
        </Box>
      ))}
    </Stack>
  );
}

export default function AnswerReceipt({ onSavedLens, methodElsewhere = false, ...props }: AnswerReceiptProps) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const contents = receiptContents(props);
  const { confidence, tablesTitle, quietFacts } = contents;
  const hasMethod = contents.hasMethod && !methodElsewhere;
  const sources = props.provenance?.tables ?? [];
  const verifiedTerms = props.provenance?.terms.filter((term) => term.verified) ?? [];
  const hasProvenance = sources.length > 0 || verifiedTerms.length > 0;
  const hasDetails = hasProvenance || hasMethod;
  const cta = hasProvenance ? 'Sources & definitions' : receiptCta(quietFacts);
  const detailTextSx = {
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 'normal',
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  };

  if (!hasMethod && quietFacts.length === 0) {
    return null;
  }

  const factLine = (
    <Typography
      component="span"
      title={tablesTitle}
      sx={{
        fontSize: 12,
        fontWeight: 400,
        letterSpacing: 'normal',
        lineHeight: 1.5,
        color: 'text.secondary',
        minWidth: 0,
        overflowWrap: 'anywhere',
        textAlign: 'left',
      }}
    >
      {quietFacts.join(' · ')}
    </Typography>
  );

  return (
    <Box data-testid="ui-stream-receipt" data-confidence={confidence ?? undefined}>
      {hasDetails ? (
        <ButtonBase
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-controls={detailsId}
          aria-label={cta}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            gap: 0.75,
            py: 0.25,
            borderRadius: '8px',
            '&:hover .receipt-cta': { color: 'text.primary' },
            '&:focus-visible': { outline: '2px solid', outlineOffset: '-2px' },
          }}
        >
          {quietFacts.length > 0 ? factLine : null}
          {quietFacts.length === 0 ? (
            <Typography component="span" className="receipt-cta" sx={{ ...detailTextSx, color: 'text.secondary' }}>
              {cta}
            </Typography>
          ) : null}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              flexShrink: 0,
              color: 'text.secondary',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: `transform ${DUR_STATE}ms ${EASE_STATE}`,
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        </ButtonBase>
      ) : (
        factLine
      )}
      {confidence === 'low' ? (
        <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: 'text.secondary', mt: 0.25 }}>
          {LOW_CONFIDENCE_CAVEAT}
        </Typography>
      ) : null}
      {hasDetails ? (
        <Collapse id={detailsId} in={open} unmountOnExit>
          {hasProvenance ? (
            <Stack spacing={1} sx={{ py: 1 }} data-testid="ui-stream-provenance">
              {sources.length > 0 ? (
                <Box>
                  <Typography sx={detailTextSx}>Sources</Typography>
                  {sources.map((source, index) => (
                    <Typography key={`${source.fqn}-${index}`} color="text.secondary" sx={detailTextSx}>
                      {source.fqn}
                      {[source.tier, source.cadence].filter(Boolean).length > 0
                        ? ` · ${[source.tier, source.cadence].filter(Boolean).join(' · ')}`
                        : ''}
                    </Typography>
                  ))}
                </Box>
              ) : null}
              {verifiedTerms.length > 0 ? (
                <Box>
                  <Typography sx={detailTextSx}>Verified definitions</Typography>
                  {verifiedTerms.map((term, index) => (
                    <Typography key={`${term.name}-${index}`} color="text.secondary" sx={detailTextSx}>
                      {term.name}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Stack>
          ) : null}
          {hasMethod ? <AnswerMethod {...props} onSavedLens={onSavedLens} /> : null}
        </Collapse>
      ) : null}
    </Box>
  );
}
